import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  DialogActions,
  Switch,
  Box,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'
import { MODES } from '../../lib/constants'
import { CustomDialogTitle } from './CustomerStyledComponents'
import './ModalStyle.scss'
import { useMutation } from '@apollo/client'
import { gqlTransporter, gqlUsers } from '../../gql'
import { transporterColors } from '../../lib/transporterColors'

function TransporterModal({ open, onClose, mode, initialData, refresh }) {
  const { t } = useTranslation('transporters')
  const [isSuccessSaved, setIsSuccessSaved] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isErrorSave, setIsErrorSave] = useState(false)
  const [errorSaveMessage, setErrorSaveMessage] = useState('')

  const emptyTransporter = {
    name: '',
    color: transporterColors[0],
  }

  const [transporter, setTransporter] = useState(emptyTransporter)
  const [contacts, setContacts] = useState([])
  const [nameError, setNameError] = useState('')
  const [drivers, setDrivers] = useState([])

  const [contactFormOpen, setContactFormOpen] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    isPrimary: false,
  })
  const [contactEmailError, setContactEmailError] = useState('')
  const [contactFirstNameError, setContactFirstNameError] = useState('')
  const [contactLastNameError, setContactLastNameError] = useState('')
  const [contactPhoneError, setContactPhoneError] = useState('')

  const [contactToDelete, setContactToDelete] = useState(null)
  const [collapsed, setCollapsed] = useState({ transporteur: false, contacts: false, chauffeurs: false })

  const toggleBlock = (block) => {
    setCollapsed((prev) => ({ ...prev, [block]: !prev[block] }))
  }

  const [createTransporter] = useMutation(gqlTransporter.CREATE_TRANSPORTER)
  const [updateTransporter] = useMutation(gqlTransporter.UPDATE_TRANSPORTER)
  const [createContact] = useMutation(gqlTransporter.CREATE_TRANSPORTER_CONTACT)
  const [updateContact] = useMutation(gqlTransporter.UPDATE_TRANSPORTER_CONTACT)
  const [deleteContact] = useMutation(gqlTransporter.DELETE_TRANSPORTER_CONTACT)
  const [updateUser] = useMutation(gqlUsers.UPDATE_USER)

  const [isSaveEnabled, setIsSaveEnabled] = useState(false)

  const resetContactForm = useCallback(() => {
    setContactForm({ firstName: '', lastName: '', email: '', phoneNumber: '', isPrimary: false })
    setContactEmailError('')
    setContactFirstNameError('')
    setContactLastNameError('')
    setContactPhoneError('')
    setEditingContact(null)
  }, [])

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setTransporter({ name: initialData.name, color: initialData.color })
      setContacts(initialData.transporterContacts || [])
      setDrivers(initialData.users || [])
    } else if (mode === 'create') {
      setTransporter(emptyTransporter)
      setContacts([])
    }
    setNameError('')
    resetContactForm()
    setContactToDelete(null)
  }, [mode, initialData, resetContactForm])

  useEffect(() => {
    setIsSaveEnabled(transporter.name && !nameError)
  }, [transporter, nameError])

  useEffect(() => {
    const isContactFormValid =
      contactForm.firstName.trim() &&
      contactForm.lastName.trim() &&
      contactForm.email.trim() &&
      contactForm.phoneNumber.trim() &&
      !contactEmailError &&
      !contactFirstNameError &&
      !contactLastNameError &&
      !contactPhoneError
    setIsContactFormValid(isContactFormValid)
  }, [contactForm, contactEmailError, contactFirstNameError, contactLastNameError, contactPhoneError])

  const [isContactFormValid, setIsContactFormValid] = useState(false)

  const displayRequiredField = (field) => {
    return t(`requiredField.${field}`)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setTransporter((prev) => ({ ...prev, [name]: value }))
    if (name === 'name') setNameError(value.trim() ? '' : displayRequiredField('name'))
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const getResultMessage = (mode, result) => {
    return t(`${result}.${mode}`)
  }

  const getContactResultMessage = (action, result) => {
    return t(`${result}.contact${action.charAt(0).toUpperCase() + action.slice(1)}`)
  }

  const handleSave = async () => {
    if (!isSaveEnabled) return
    if (!transporter.name) {
      setNameError(displayRequiredField('name'))
      return
    }

    try {
      if (mode === MODES.CREATE) {
        const result = await createTransporter({
          variables: {
            color: transporter.color,
            name: transporter.name,
          },
        })
        const transporterId = result.data.createTransporter.transporter.id

        for (const contact of contacts) {
          await createContact({
            variables: {
              transporterId,
              firstName: contact.firstName,
              lastName: contact.lastName,
              email: contact.email,
              phoneNumber: contact.phoneNumber,
              isPrimary: contact.isPrimary,
            },
          })
        }
      }

      if (mode === MODES.EDIT) {
        await updateTransporter({
          variables: {
            color: transporter.color,
            name: transporter.name,
            id: initialData.id,
          },
        })
      }

      setSuccessMessage(getResultMessage(mode, 'success'))
      setIsSuccessSaved(true)
      setTimeout(() => {
        setIsSuccessSaved(false)
        onClose()
        refresh()
      }, 2000)
    } catch (err) {
      console.error('ApolloError:', err)
      setErrorSaveMessage(getResultMessage(mode, 'error'))
      setIsErrorSave(true)
      setTimeout(() => {
        setIsErrorSave(false)
        onClose()
      }, 2000)
    }
  }

  const handleOpenAddContact = () => {
    resetContactForm()
    setContactFormOpen(true)
  }

  const handleOpenEditContact = (contact) => {
    setEditingContact(contact)
    setContactForm({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      isPrimary: contact.isPrimary,
    })
    setContactEmailError('')
    setContactFirstNameError('')
    setContactLastNameError('')
    setContactPhoneError('')
    setContactFormOpen(true)
  }

  const handleCloseContactForm = () => {
    setContactFormOpen(false)
    resetContactForm()
  }

  const handleContactFormChange = (e) => {
    const { name, value } = e.target
    setContactForm((prev) => ({ ...prev, [name]: value }))
    if (name === 'email') {
      setContactEmailError(value.trim() && !validateEmail(value) ? t('invalidEmail') : '')
    }
    if (name === 'firstName')
      setContactFirstNameError(value.trim() ? '' : displayRequiredField('firstName'))
    if (name === 'lastName')
      setContactLastNameError(value.trim() ? '' : displayRequiredField('lastName'))
    if (name === 'phoneNumber')
      setContactPhoneError(value.trim() ? '' : displayRequiredField('phoneNumber'))
  }

  const handleSaveContact = async () => {
    if (!isContactFormValid) {
      setContactFirstNameError(!contactForm.firstName.trim() ? displayRequiredField('firstName') : '')
      setContactLastNameError(!contactForm.lastName.trim() ? displayRequiredField('lastName') : '')
      setContactPhoneError(!contactForm.phoneNumber.trim() ? displayRequiredField('phoneNumber') : '')
      if (!contactForm.email.trim()) {
        setContactEmailError(displayRequiredField('email'))
      } else if (!validateEmail(contactForm.email)) {
        setContactEmailError(t('invalidEmail'))
      }
      return
    }

    try {
      if (editingContact) {
        await updateContact({
          variables: {
            id: editingContact.id,
            patch: {
              firstName: contactForm.firstName,
              lastName: contactForm.lastName,
              email: contactForm.email,
              phoneNumber: contactForm.phoneNumber,
            },
          },
        })
        setContacts((prev) =>
          prev.map((c) =>
            c.id === editingContact.id
              ? { ...c, firstName: contactForm.firstName, lastName: contactForm.lastName, email: contactForm.email, phoneNumber: contactForm.phoneNumber }
              : c
          )
        )
        setSuccessMessage(getContactResultMessage('edit', 'success'))
      } else if (mode === MODES.EDIT && initialData) {
        const result = await createContact({
          variables: {
            transporterId: initialData.id,
            firstName: contactForm.firstName,
            lastName: contactForm.lastName,
            email: contactForm.email,
            phoneNumber: contactForm.phoneNumber,
            isPrimary: false,
          },
        })
        const newContact = {
          id: result.data.createTransporterContact.transporterContact.id,
          firstName: contactForm.firstName,
          lastName: contactForm.lastName,
          email: contactForm.email,
          phoneNumber: contactForm.phoneNumber,
          isPrimary: false,
          isActive: true,
        }
        setContacts((prev) => [...prev, newContact])
        setSuccessMessage(getContactResultMessage('create', 'success'))
      } else {
        setContacts((prev) => [
          ...prev,
          {
            firstName: contactForm.firstName,
            lastName: contactForm.lastName,
            email: contactForm.email,
            phoneNumber: contactForm.phoneNumber,
            isPrimary: false,
            id: `temp_${Date.now()}`,
          },
        ])
        setSuccessMessage(getContactResultMessage('create', 'success'))
      }

      setIsSuccessSaved(true)
      setContactFormOpen(false)
      resetContactForm()
      setTimeout(() => setIsSuccessSaved(false), 2000)
    } catch (err) {
      console.error('ApolloError:', err)
      setErrorSaveMessage(getContactResultMessage(editingContact ? 'edit' : 'create', 'error'))
      setIsErrorSave(true)
      setTimeout(() => setIsErrorSave(false), 2000)
    }
  }

  const handleDeleteContact = async (contactId) => {
    try {
      setContacts((prev) => prev.filter((c) => c.id !== contactId))
      if (!contactId.toString().startsWith('temp_')) {
        await deleteContact({ variables: { id: contactId } })
      }
      setSuccessMessage(getContactResultMessage('delete', 'success'))
      setIsSuccessSaved(true)
      setContactToDelete(null)
      setTimeout(() => setIsSuccessSaved(false), 2000)
    } catch (err) {
      console.error('ApolloError:', err)
      setErrorSaveMessage(getContactResultMessage('delete', 'error'))
      setIsErrorSave(true)
      setTimeout(() => setIsErrorSave(false), 2000)
    }
  }

  const handleSetPrimary = async (contact) => {
    try {
      setContacts((prev) =>
        prev.map((c) => ({ ...c, isPrimary: c.id === contact.id }))
      )
      if (!contact.id.toString().startsWith('temp_')) {
        await updateContact({
          variables: {
            id: contact.id,
            patch: { isPrimary: true },
          },
        })
      }
    } catch (err) {
      console.error('ApolloError:', err)
    }
  }

  const handleToggleDriverActive = async (driverId, currentActive) => {
    try {
      await updateUser({
        variables: { id: driverId, isActive: !currentActive },
      })
      setDrivers((prev) =>
        prev.map((u) =>
          u.id === driverId ? { ...u, isActive: !currentActive } : u
        )
      )
    } catch (err) {
      console.error('Error toggling driver status:', err)
    }
  }

  const handleColorChange = (color) => {
    setTransporter((prev) => ({ ...prev, color }))
  }

  const orderedContacts = contacts

  const blockSx = {
    borderRadius: '4px',
    border: '1px solid #C4C4C4',
    display: 'flex',
    padding: '24px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
    alignSelf: 'stretch',
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ minWidth: 500 }}>
      <CustomDialogTitle>
        <div>
          <h5>{mode === MODES.CREATE ? t('addNewTransporter') : t('editTransporter')}</h5>
        </div>
        <Button style={{ color: 'inherit', fontSize: '1rem' }} onClick={onClose}>
          X
        </Button>
      </CustomDialogTitle>
      {isSuccessSaved && <Box className="success-message">{successMessage}</Box>}
      {isErrorSave && <Box className="error-message">{errorSaveMessage}</Box>}
      <DialogContent className="little-margin-top">
        <Grid container spacing={2} className="little-margin-top">
          <Grid item xs={12}>
            {mode === MODES.CREATE && (
              <>
                <TextField
                  label={
                    <span>
                      {t('transporterName')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  fullWidth
                  name="name"
                  value={transporter.name}
                  onChange={handleInputChange}
                  error={!!nameError}
                  helperText={nameError}
                />

                <Typography className="little-margin-top" variant="h6">
                  {t('chooseColor')}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {transporterColors.map((color) => (
                    <Grid item key={color}>
                      <IconButton
                        style={{
                          backgroundColor: color,
                          border: transporter.color === color ? '2px solid #000' : '1px solid #ccc',
                          width: '32px',
                          height: '32px',
                        }}
                        onClick={() => handleColorChange(color)}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Typography className="little-margin-top" variant="h6">
                  {t('contacts')}
                </Typography>
                {contacts.length > 0 && (
                  <TableContainer sx={{ width: '100%', mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderPrincipal')}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderName')}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderEmail')}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderPhone')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderedContacts.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell>
                              <Radio checked={contact.isPrimary} size="small" disabled sx={{ position: 'static' }} />
                            </TableCell>
                            <TableCell>{contact.firstName} {contact.lastName}</TableCell>
                            <TableCell>{contact.email}</TableCell>
                            <TableCell>{contact.phoneNumber}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenAddContact}
                  sx={{ mt: 2 }}
                >
                  {t('addContact')}
                </Button>
              </>
            )}

            {mode === MODES.EDIT && (
              <>
                <Box sx={blockSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6">Transporteur</Typography>
                    <Button size="small" onClick={() => toggleBlock('transporteur')} sx={{ minWidth: 'auto' }}>
                      {collapsed.transporteur ? '▶' : '▼'}
                    </Button>
                  </Box>
                  {!collapsed.transporteur && (
                    <>
                      <TextField
                        label={
                          <span>
                            {t('transporterName')} <span style={{ color: 'red' }}>*</span>
                          </span>
                        }
                        fullWidth
                        name="name"
                        value={transporter.name}
                        onChange={handleInputChange}
                        error={!!nameError}
                        helperText={nameError}
                      />
                      <Typography variant="body2">{t('chooseColor')}</Typography>
                      <Grid container spacing={1}>
                        {transporterColors.map((color) => (
                          <Grid item key={color}>
                            <IconButton
                              style={{
                                backgroundColor: color,
                                border: transporter.color === color ? '2px solid #000' : '1px solid #ccc',
                                width: '32px',
                                height: '32px',
                              }}
                              onClick={() => handleColorChange(color)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Box>

                <Box sx={{ ...blockSx, mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6">{t('contacts')}</Typography>
                    <Button size="small" onClick={() => toggleBlock('contacts')} sx={{ minWidth: 'auto' }}>
                      {collapsed.contacts ? '▶' : '▼'}
                    </Button>
                  </Box>
                  {!collapsed.contacts && (
                    <>
                      <TableContainer sx={{ width: '100%' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderPrincipal')}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderName')}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderEmail')}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderPhone')}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{t('tableHeaderActions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orderedContacts.map((contact) => (
                              <TableRow key={contact.id}>
                                <TableCell>
                                  <Radio
                                    checked={contact.isPrimary}
                                    onChange={() => {
                                      if (!contact.isPrimary) handleSetPrimary(contact)
                                    }}
                                    size="small"
                                    sx={{ position: 'static' }}
                                  />
                                </TableCell>
                                <TableCell>{contact.firstName} {contact.lastName}</TableCell>
                                <TableCell>{contact.email}</TableCell>
                                <TableCell>{contact.phoneNumber}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: '8px' }}>
                                    <IconButton size="small" onClick={() => setContactToDelete(contact)} sx={{ position: 'relative', width: '34px', height: '34px' }}>
                                      <Delete fontSize="small" sx={{ position: 'relative', fill: '#EB6B1B', stroke: 'inherit' }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleOpenEditContact(contact)} sx={{ position: 'relative', width: '34px', height: '34px' }}>
                                      <Edit fontSize="small" sx={{ position: 'relative', fill: '#1577FC', stroke: 'inherit' }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Button variant="outlined" color="primary" onClick={handleOpenAddContact} sx={{ alignSelf: 'flex-end' }}>
                        {t('addContact')}
                      </Button>
                    </>
                  )}
                </Box>

                <Box sx={{ ...blockSx, mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6">Chauffeurs</Typography>
                    <Button size="small" onClick={() => toggleBlock('chauffeurs')} sx={{ minWidth: 'auto' }}>
                      {collapsed.chauffeurs ? '▶' : '▼'}
                    </Button>
                  </Box>
                  {!collapsed.chauffeurs && (
                    <>
                      {drivers
                        .filter((driver) => driver.companyRole === 'Chauffeur permanent')
                        .map((driver) => (
                          <Box
                            key={driver.id}
                            sx={{
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              p: 1.5,
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography variant="body1">
                                {driver.firstname} {driver.lastname}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {driver.licensePlate}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ mr: 0.5 }}>
                                {driver.isActive !== false ? t('driverActive') : t('driverInactive')}
                              </Typography>
                              <Switch
                                checked={driver.isActive !== false}
                                onChange={() => handleToggleDriverActive(driver.id, driver.isActive)}
                                color="primary"
                              />
                            </Box>
                          </Box>
                        ))}
                    </>
                  )}
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="warning">
          {t('cancel')}
        </Button>
        <Button disabled={!isSaveEnabled} onClick={handleSave} variant="contained" color="primary">
          {t('save')}
        </Button>
      </DialogActions>

      <Dialog open={contactFormOpen} onClose={handleCloseContactForm} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingContact ? t('editContact') : t('addContact')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={
              <span>
                {t('firstNameContact')} <span style={{ color: 'red' }}>*</span>
              </span>
            }
            fullWidth
            name="firstName"
            value={contactForm.firstName}
            onChange={handleContactFormChange}
            error={!!contactFirstNameError}
            helperText={contactFirstNameError}
            sx={{ mt: 2 }}
          />
          <TextField
            label={
              <span>
                {t('lastNameContact')} <span style={{ color: 'red' }}>*</span>
              </span>
            }
            fullWidth
            name="lastName"
            value={contactForm.lastName}
            onChange={handleContactFormChange}
            error={!!contactLastNameError}
            helperText={contactLastNameError}
            sx={{ mt: 2 }}
          />
          <TextField
            label={
              <span>
                {t('email')} <span style={{ color: 'red' }}>*</span>
              </span>
            }
            fullWidth
            name="email"
            value={contactForm.email}
            error={!!contactEmailError}
            helperText={contactEmailError}
            onChange={handleContactFormChange}
            sx={{ mt: 2 }}
          />
          <TextField
            label={
              <span>
                {t('phoneNumber')} <span style={{ color: 'red' }}>*</span>
              </span>
            }
            fullWidth
            name="phoneNumber"
            value={contactForm.phoneNumber}
            onChange={(e) => {
              const value = e.target.value
              if (/^\d*$/.test(value)) {
                handleContactFormChange(e)
              }
            }}
            error={!!contactPhoneError}
            helperText={contactPhoneError}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactForm} variant="contained" color="warning">
            {t('cancel')}
          </Button>
          <Button
            disabled={!isContactFormValid}
            onClick={handleSaveContact}
            variant="contained"
            color="primary"
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!contactToDelete} onClose={() => setContactToDelete(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('deleteContact')}</DialogTitle>
        <DialogContent>
          {contactToDelete && (
            <>
              <Typography sx={{ mb: 2 }}>{t('confirmDeleteContact')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{t('lastNameContact')} :</strong> {contactToDelete.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('firstNameContact')} :</strong> {contactToDelete.firstName}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('email')} :</strong> {contactToDelete.email}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('phoneNumber')} :</strong> {contactToDelete.phoneNumber}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactToDelete(null)} variant="contained" color="warning">
            {t('cancel')}
          </Button>
          <Button onClick={() => handleDeleteContact(contactToDelete?.id)} color="error" variant="contained">
            {t('deleteContact')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

TransporterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initialData: PropTypes.object,
  refresh: PropTypes.func.isRequired,
}

export default TransporterModal
